/**
 * BellPreviewScreen — Full resonance view for a single bell.
 *
 * M3-UX-P1-001: Visual resonance figure + bell metadata.
 * UI-INV-005: Leaf component, props only, no engine imports.
 *
 * Four senses:
 *   Sight  : concentric rings animate outward on mount
 *   Sound  : parent plays bell on entry; tap circle to hear again
 *   Touch  : tap circle + button to set default
 *   Silence: no text labels on the rings
 */
import type { BellEntry } from "../data/bellCatalogue";
import { BellIcon, BELL_ICON_SIZE } from "../components/BellIcon";
import styles from "./BellPreviewScreen.module.css";
import { TEST_IDS } from "../testing/testIds";

// ── SVG resonance constants ───────────────────────────────────────────────────

const SVG_SIZE          = 240;
const RING_RADII        = [100, 78, 56, 34] as const;
const RING_BASE_OPACITY = 0.8;
const RING_OPACITY_STEP = 0.18;
const RING_DELAY_STEP_S = 0.12;

// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  bell: BellEntry;
  isDefault: boolean;
  onPlay: () => void;
  onSetDefault: () => void;
  onBack: () => void;
};

export function BellPreviewScreen({
  bell,
  isDefault,
  onPlay,
  onSetDefault,
  onBack,
}: Props) {
  return (
    <div className={styles.screen}>
      <div className={styles.topBar}>
        <button className={styles.back} onClick={onBack} aria-label="Back" data-testid={TEST_IDS.BACK_BTN}>
          ← back
        </button>
        <span className={styles.tradition}>{bell.tradition}</span>
      </div>

      <div className={styles.resonance}>
        <button
          className={styles.resonanceBtn}
          onClick={onPlay}
          aria-label={`Tap to hear ${bell.name}`}
          type="button"
        >
          <svg
            width={SVG_SIZE}
            height={SVG_SIZE}
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            className={styles.rings}
            aria-hidden="true"
          >
            {RING_RADII.map((r, i) => (
              <circle
                key={r}
                cx={SVG_SIZE / 2}
                cy={SVG_SIZE / 2}
                r={r}
                fill="none"
                stroke="var(--rc-clay)"
                strokeWidth="1"
                opacity={RING_BASE_OPACITY - i * RING_OPACITY_STEP}
                className={styles.ring}
                style={{ animationDelay: `${i * RING_DELAY_STEP_S}s` }}
              />
            ))}
          </svg>
          <BellIcon id={bell.id} size={BELL_ICON_SIZE.PREVIEW} className={styles.bellGlyph} />
        </button>
      </div>

      <div className={styles.meta}>
        <div className={styles.bellName}>{bell.name}</div>
        <div className={styles.bellTraditionalName}>{bell.traditionalName}</div>
        <div className={styles.bellCharacter}>{bell.character}</div>
        <div className={styles.bellDetails}>
          {bell.use} · {bell.decayLabel} decay
        </div>

        <button
          className={`${styles.setDefault} ${isDefault ? styles.setDefaultActive : ""}`}
          onClick={onSetDefault}
          type="button"
          data-testid={TEST_IDS.BELL_PREVIEW_SET_DEFAULT_BTN}
        >
          {isDefault ? "default bell" : "set as default"}
        </button>
      </div>
    </div>
  );
}
